import {
  LibraryPrivacy,
  PaginatedListOfExerciseAssetDto,
} from "@growth-machine-llc/stridist-api-client";
import { useOptimisticUpdateMutation } from "../../../hooks/useOptimisticUpdateMutation";
import { useQueryClient } from "@tanstack/react-query";
import { COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY } from "../ExercisesTableScreen";
import ExerciseAssetsService from "../../../services/ExerciseAssetsService";
import { INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE } from "./useArchiveRestoreLibraryExercise";
import { AllOption, type LibraryPrivacyFilter } from "../../../constants";

const getTypesToInvalidate = (privacyType: LibraryPrivacyFilter) => {
  return [AllOption, LibraryPrivacy.TEAM, LibraryPrivacy.PRIVATE].filter(
    (t) => t !== privacyType,
  );
};

export const useToggleTeamExerciseVisibilityMutation = (
  privacy: LibraryPrivacyFilter,
  disableToastAlerts: boolean = false,
) => {
  const queryClient = useQueryClient();

  const exerciseTypesToInvalidate = getTypesToInvalidate(privacy);

  return useOptimisticUpdateMutation({
    queryKey: [COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY, { privacy }],
    invalidateQueryOptions: {
      enabled: true,
      refetchType: "none",
    },
    disableToastAlerts,
    mutationFn: ExerciseAssetsService.toggleTeamExerciseVisibility,
    optimisticUpdater: {
      updateFn: (oldData: PaginatedListOfExerciseAssetDto, variables) => {
        const shouldRemoveExercise =
          privacy === LibraryPrivacy.TEAM && !variables.isTeamShared;

        const items = shouldRemoveExercise
          ? oldData.items.filter((ex) => ex.id !== variables.id)
          : oldData.items.map((ex) =>
              ex.id === variables.id
                ? { ...ex, isTeamShared: variables.isTeamShared }
                : ex,
            );

        return { ...oldData, items } as PaginatedListOfExerciseAssetDto;
      },
    },

    options: {
      onSuccess: () => {
        exerciseTypesToInvalidate.forEach((type) => {
          queryClient.invalidateQueries({
            queryKey: [
              COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY,
              { exerciseType: type },
            ],
            exact: false,
          });
        });

        INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE.forEach((queryKey) => {
          queryClient.invalidateQueries({
            queryKey: [queryKey],
            exact: false,
          });
        });
      },
    },
  });
};
